import React, { useCallback } from "react"
import types from "prop-types"
import Formsy from "formsy-react"
import {
  useCustomName,
  useModal,
  useSCIMResourceManagement,
  useSubmit,
} from "hooks"
import { DEPARTMENT_KEY } from "utils/customNameHelpers"
import * as API from "services/api"
import Modal, { ModalHeader } from "shared/Modal"
import { PrimaryButton, Button } from "shared/buttons"
import { successToast, errorToast } from "shared/toast"
import ExternalIdentifier from "./Department/Fields/ExternalIdentifier"
import Name from "./Department/Fields/Name"

const CreateDepartmentModal = ({ facilityId = undefined, onDepartmentCreated }) => {
  const {
    name: departmentName,
    nameInSentence: departmentNameInSentence,
  } = useCustomName(DEPARTMENT_KEY)

  const { departmentsManagedBySCIM } = useSCIMResourceManagement()

  const { modalIsOpen, openModal, closeModal } = useModal()
  const { canSubmit, enableSubmit, disableSubmit } = useSubmit(false)

  const createDepartment = useCallback(async (model) => {
    const response = await API.createFacilityDepartment({ facilityId, department: model })

    if (response.ok) {
      onDepartmentCreated(response.data)
      closeModal()
      successToast(`${departmentName} created successfully!`)
    } else {
      console.error("Error updating department: ", response)
      errorToast(`Something went wrong. Unable to create ${departmentNameInSentence}.`, response)
    }
  }, [facilityId, onDepartmentCreated, closeModal, departmentName, departmentNameInSentence])

  if (!facilityId) return null

  return (
    <>
      <Button
        className="text-focus border border-light my-4"
        text={`+ Add ${departmentName}`}
        onClick={openModal}
      />

      <Modal
        isOpen={modalIsOpen}
        aria={{ label: `new ${departmentNameInSentence}` }}
      >
        <ModalHeader closeModal={closeModal} heading={`New ${departmentName}`} />
        <Formsy
          onSubmit={createDepartment}
          onValid={enableSubmit}
          onInvalid={disableSubmit}
        >
          <div className="w-3/4 mx-auto grid gap-3">
            <Name />
            {
              departmentsManagedBySCIM && <ExternalIdentifier />
            }
            <div className="flex justify-center mt-8">
              <Button
                text="Cancel"
                onClick={closeModal}
                className="border border-light-300 mr-2"
              />
              <PrimaryButton
                text="Create"
                type="submit"
                disabled={!canSubmit}
              />
            </div>
          </div>
        </Formsy>
      </Modal>
    </>
  )
}

CreateDepartmentModal.propTypes = {
  facilityId: types.number,
  onDepartmentCreated: types.func.isRequired,
}

export default CreateDepartmentModal
